import React, { useState, useEffect, useCallback } from 'react'
import { Platform, useWindowDimensions } from 'react-native'
import { Search, X, MapPin, ChevronRight, SlidersHorizontal, Store, Clock } from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import {
  SearchInput, SectionHeader, ListItem, Chip, ChipText, Card, Text, Input,
  EmptyState, Spinner, ProductCard, StoreCard, FilterTabs, IconButton, Button, ButtonText,
  FadingScrollView,
  YStack, XStack, ScrollView,
} from '@fliisi/pollen'
import { ScreenLayout } from '../components/ScreenLayout'
import AsyncStorage from '@react-native-async-storage/async-storage'

const RECENT_SEARCHES_KEY = 'fliisi_recent_searches'
const MAX_RECENT = 8

interface SearchResult {
  id: string
  title: string
  price: number
  imageUrl: string | null
  storeName: string | null
  storeId: string | null
}

interface StoreResult {
  id: string
  name: string
  location: string | null
  productCount: number
}

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SearchScreen'>
}

type SortOption = 'relevance' | 'newest' | 'price_asc' | 'price_desc'

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Oletus' },
  { value: 'newest', label: 'Uusimmat' },
  { value: 'price_asc', label: 'Halvin' },
  { value: 'price_desc', label: 'Kallein' },
]

const CONDITIONS = [
  { value: 'new', labelKey: 'condition_new' },
  { value: 'like_new', labelKey: 'condition_like_new' },
  { value: 'good', labelKey: 'condition_good' },
  { value: 'fair', labelKey: 'condition_fair' },
  { value: 'poor', labelKey: 'condition_poor' },
]

export default function SearchScreen({ navigation }: Props) {
  const { width: screenWidth } = useWindowDimensions()
  const { t, language } = useLanguage()

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [storeResults, setStoreResults] = useState<StoreResult[]>([])
  const [searching, setSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [defaultProducts, setDefaultProducts] = useState<SearchResult[]>([])
  const [totalProducts, setTotalProducts] = useState(0)
  const [totalStores, setTotalStores] = useState(0)

  const [sortBy, setSortBy] = useState<SortOption>('relevance')
  const [showFilters, setShowFilters] = useState(false)
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [selectedConditions, setSelectedConditions] = useState<string[]>([])

  const hasActiveFilters = sortBy !== 'relevance' || minPrice !== '' || maxPrice !== '' || selectedConditions.length > 0

  useEffect(() => {
    loadRecentSearches()
    loadStats()
    fetchDefaultProducts()
  }, [])

  const fetchDefaultProducts = async () => {
    try {
      const { data } = await supabase
        .from('products')
        .select(`
          id, title, title_fi, title_en, price,
          product_images(image_url),
          flea_market:flea_markets!products_flea_market_id_fkey(id, name)
        `)
        .eq('status', 'for_sale')
        .order('created_at', { ascending: false })
        .limit(24)
      if (data) {
        setDefaultProducts(data.map((p: any) => {
          const market = Array.isArray(p.flea_market) ? p.flea_market[0] : p.flea_market
          return {
            id: p.id,
            title: (language === 'fi' ? p.title_fi : p.title_en) || p.title || '',
            price: p.price || 0,
            imageUrl: p.product_images?.[0]?.image_url || null,
            storeName: market?.name || null,
            storeId: market?.id || null,
          }
        }))
      }
    } catch (e) { console.error('Default products error:', e) }
  }

  const loadStats = async () => {
    try {
      const [{ count: pCount }, { count: sCount }] = await Promise.all([
        supabase.from('products').select('*', { count: 'exact', head: true }).eq('status', 'for_sale'),
        supabase.from('flea_markets').select('*', { count: 'exact', head: true }),
      ])
      setTotalProducts(pCount || 0)
      setTotalStores(sCount || 0)
    } catch (e) { console.error('Stats error:', e) }
  }

  const loadRecentSearches = async () => {
    try {
      const stored = await AsyncStorage.getItem(RECENT_SEARCHES_KEY)
      if (stored) setRecentSearches(JSON.parse(stored))
    } catch {}
  }

  const saveRecentSearch = async (term: string) => {
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, MAX_RECENT)
    setRecentSearches(updated)
    await AsyncStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated)).catch(() => {})
  }

  const clearRecentSearches = async () => {
    setRecentSearches([])
    await AsyncStorage.removeItem(RECENT_SEARCHES_KEY).catch(() => {})
  }

  const toggleCondition = (value: string) => {
    setSelectedConditions(prev =>
      prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]
    )
  }

  const doSearch = useCallback(async (text: string, sort: SortOption = sortBy) => {
    const trimmed = text.trim()
    if (trimmed.length < 2) {
      setResults([]); setStoreResults([]); setHasSearched(false)
      return
    }

    setSearching(true); setHasSearched(true)
    saveRecentSearch(trimmed)

    try {
      const term = `%${trimmed}%`

      let pq = supabase
        .from('products')
        .select(`
          id, title, title_fi, title_en, price, created_at, condition_grade,
          product_images(image_url),
          flea_market:flea_markets!products_flea_market_id_fkey(id, name)
        `)
        .eq('status', 'for_sale')
        .or(`title.ilike.${term},title_fi.ilike.${term},title_en.ilike.${term},description.ilike.${term}`)

      if (minPrice) pq = pq.gte('price', parseFloat(minPrice))
      if (maxPrice) pq = pq.lte('price', parseFloat(maxPrice))
      if (selectedConditions.length > 0) pq = pq.in('condition_grade', selectedConditions)

      switch (sort) {
        case 'price_asc': pq = pq.order('price', { ascending: true }); break
        case 'price_desc': pq = pq.order('price', { ascending: false }); break
        default: pq = pq.order('created_at', { ascending: false })
      }
      pq = pq.limit(30)

      const sq = supabase.from('flea_markets').select('id, name, location').ilike('name', term).limit(5)

      const [{ data: products }, { data: stores }] = await Promise.all([pq, sq])

      if (products) {
        setResults(products.map((p: any) => {
          const market = Array.isArray(p.flea_market) ? p.flea_market[0] : p.flea_market
          return {
            id: p.id,
            title: (language === 'fi' ? p.title_fi : p.title_en) || p.title || '',
            price: p.price || 0,
            imageUrl: p.product_images?.[0]?.image_url || null,
            storeName: market?.name || null,
            storeId: market?.id || null,
          }
        }))
      }

      if (stores) {
        const storeIds = stores.map((s: any) => s.id)
        const { data: sc } = await supabase
          .from('products').select('flea_market_id')
          .eq('status', 'for_sale').in('flea_market_id', storeIds)

        const cm: Record<string, number> = {}
        sc?.forEach((p: any) => { cm[p.flea_market_id] = (cm[p.flea_market_id] || 0) + 1 })

        setStoreResults(stores.map((s: any) => ({
          id: s.id, name: s.name, location: s.location, productCount: cm[s.id] || 0,
        })))
      }
    } catch (e) { console.error('Search error:', e) }
    finally { setSearching(false) }
  }, [sortBy, minPrice, maxPrice, selectedConditions, language])

  const clearSearch = () => {
    setQuery('')
    setResults([]); setStoreResults([])
    setHasSearched(false)
  }

  const clearFilters = () => {
    setSortBy('relevance')
    setMinPrice('')
    setMaxPrice('')
    setSelectedConditions([])
  }

  const numColumns = screenWidth > 500 ? 3 : 2
  // Percentage-based widths are reliable across web/native regardless of container pixel width.
  // For 2 cols with 12px gap inside a padded container: each card ≈ 48.5% of XStack width.
  // For 3 cols: ≈ 31.5%.
  const cardWidth = numColumns === 3 ? '31.5%' : '48.5%'

  return (
    <YStack f={1} bg="$background">
      <ScrollView
        f={1}
        contentContainerStyle={{ maxWidth: 768, alignSelf: 'center', width: '100%', paddingBottom: 16 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <YStack px="$lg" pt={Platform.OS === 'web' ? 20 : 56} pb="$sm">
          <Text fontFamily="$cursive" fontSize={28} color="$color">{t('search')}</Text>
          {totalProducts > 0 && (
            <Text preset="caption" tone="secondary" mt={2} mb="$sm">
              {totalProducts} {t('products')} · {totalStores} {t('stores') || 'kauppaa'}
            </Text>
          )}

          <XStack gap="$sm">
            <YStack f={1}>
              <SearchInput
                value={query}
                onChangeText={(text) => {
                  setQuery(text)
                  if (text.length === 0) clearSearch()
                }}
                onSubmitEditing={() => doSearch(query)}
                placeholder={t('search_products_stores')}
              />
            </YStack>
            <YStack>
              <IconButton
                size="md"
                variant={showFilters ? 'primary' : 'ghost'}
                onPress={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal size={18} color={showFilters ? '#fff' : '#748C70'} />
              </IconButton>
              {hasActiveFilters && !showFilters && (
                <YStack
                  pos="absolute" top={4} right={4}
                  w={8} h={8} br="$full" bg="$colorPrimary"
                  pointerEvents="none"
                />
              )}
            </YStack>
          </XStack>

          {showFilters && (
            <Card variant="elevated" mt="$sm" p="$md" gap="$md">
              {/* Sort */}
              <YStack gap="$xs">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                  {t('sort_by') || 'Järjestys'}
                </Text>
                <FilterTabs
                  options={SORT_OPTIONS}
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as SortOption)}
                  scrollable
                />
              </YStack>

              {/* Price range */}
              <YStack gap="$xs">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                  {t('price_range')}
                </Text>
                <XStack gap="$sm" ai="center">
                  <Input
                    f={1}
                    value={minPrice}
                    onChangeText={setMinPrice}
                    placeholder={t('min_price') || 'Min €'}
                    keyboardType="numeric"
                  />
                  <Text preset="bodySmall" tone="secondary">–</Text>
                  <Input
                    f={1}
                    value={maxPrice}
                    onChangeText={setMaxPrice}
                    placeholder={t('max_price') || 'Max €'}
                    keyboardType="numeric"
                  />
                </XStack>
              </YStack>

              {/* Condition */}
              <YStack gap="$xs">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                  {t('condition')}
                </Text>
                <XStack flexWrap="wrap" gap="$sm">
                  {CONDITIONS.map(c => {
                    const active = selectedConditions.includes(c.value)
                    return (
                      <Chip
                        key={c.value}
                        size="sm"
                        bg={active ? '$colorPrimary' : '$backgroundSecondary'}
                        onPress={() => toggleCondition(c.value)}
                      >
                        <ChipText color={active ? 'white' : '$color'}>{t(c.labelKey as any)}</ChipText>
                      </Chip>
                    )
                  })}
                </XStack>
              </YStack>

              {/* Actions */}
              <XStack gap="$sm">
                {hasActiveFilters && (
                  <Button variant="secondary" size="sm" f={1} onPress={clearFilters}>
                    <ButtonText variant="secondary">{t('clear') || 'Tyhjennä'}</ButtonText>
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  f={1}
                  onPress={() => { setShowFilters(false); if (query.trim().length >= 2) doSearch(query) }}
                >
                  <ButtonText>{t('search') || 'Hae'}</ButtonText>
                </Button>
              </XStack>
            </Card>
          )}
        </YStack>

        {/* Pre-search content */}
        {!hasSearched && (
          <>
            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <YStack px="$lg" mb="$md">
                <XStack jc="space-between" ai="center" mb="$sm">
                  <XStack ai="center" gap={6}>
                    <Clock size={14} color="#858B80" />
                    <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                      {t('recent_searches')}
                    </Text>
                  </XStack>
                  <Text preset="caption" tone="secondary" cursor="pointer" onPress={clearRecentSearches}>
                    {t('clear') || 'Tyhjennä'}
                  </Text>
                </XStack>
                <XStack flexWrap="wrap" gap="$sm">
                  {recentSearches.map(term => (
                    <Chip key={term} onPress={() => { setQuery(term); doSearch(term) }}>
                      <ChipText>{term}</ChipText>
                    </Chip>
                  ))}
                </XStack>
              </YStack>
            )}

            {/* Map CTA */}
            <ListItem
              icon={
                <YStack w={44} h={44} br={12} bg="$colorPrimaryMuted" jc="center" ai="center">
                  <MapPin size={22} color="#748C70" />
                </YStack>
              }
              title={t('search_nearby')}
              subtitle={t('find_stores_on_map')}
              right={<ChevronRight size={18} color="#858B80" />}
              onPress={() => navigation.navigate('MapBrowse')}
              mx="$lg" bg="$backgroundCard" br="$lg" p="$md" mb="$md"
            />

            {/* Default product grid */}
            {defaultProducts.length > 0 && (
              <YStack px="$lg">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5} mb="$sm">
                  {t('newest') || 'Uusimmat'}
                </Text>
                <XStack flexWrap="wrap" gap={12} w="100%">
                  {defaultProducts.map(item => (
                    <ProductCard
                      key={item.id}
                      w={cardWidth}
                      imageUri={item.imageUrl}
                      title={item.title}
                      price={`${item.price.toFixed(2)} €`}
                      shopName={item.storeName || undefined}
                      onPress={() => navigation.navigate('ProductView', { productId: item.id })}
                    />
                  ))}
                </XStack>
              </YStack>
            )}
          </>
        )}

        {/* Loading */}
        {searching && (
          <YStack ai="center" py="$2xl" gap="$sm">
            <Spinner size="large" />
            <Text preset="bodySmall" tone="secondary">{t('searching') || 'Haetaan...'}</Text>
          </YStack>
        )}

        {/* Results */}
        {hasSearched && !searching && (
          <>
            {storeResults.length > 0 && (
              <YStack px="$lg" mb="$md" gap="$sm">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                  {t('stores') || 'KAUPAT'}
                </Text>
                {storeResults.map(store => (
                  <StoreCard
                    key={store.id}
                    name={store.name}
                    icon={<Store size={20} color="#748C70" />}
                    details={`${store.productCount} ${t('products')}${store.location ? ` · ${store.location}` : ''}`}
                    right={<ChevronRight size={16} color="#858B80" />}
                    onPress={() => navigation.navigate('StoreBrowse', { marketId: store.id, marketName: store.name })}
                  />
                ))}
              </YStack>
            )}

            <YStack px="$lg" mb="$md">
              <XStack ai="center" gap={10} mb="$sm" flexWrap="wrap">
                <Text preset="caption" tone="muted" textTransform="uppercase" letterSpacing={0.5}>
                  {results.length > 0
                    ? `${results.length} ${t('products')}`
                    : t('no_results_found')
                  }
                </Text>
                {/* Active filter chips */}
                {selectedConditions.map(c => (
                  <Chip key={c} size="sm" onPress={() => toggleCondition(c)}>
                    <ChipText>{t(`condition_${c}` as any)}</ChipText>
                    <X size={12} color="#748C70" />
                  </Chip>
                ))}
                {(minPrice || maxPrice) && (
                  <Chip size="sm" onPress={() => { setMinPrice(''); setMaxPrice('') }}>
                    <ChipText>{minPrice || '0'} – {maxPrice || '∞'} €</ChipText>
                    <X size={12} color="#748C70" />
                  </Chip>
                )}
              </XStack>

              {results.length === 0 ? (
                <EmptyState
                  icon={<Search size={48} color="#E0E2DB" />}
                  title={`${t('no_results_found')} "${query}"`}
                  description={t('try_different_keywords')}
                />
              ) : (
                <XStack flexWrap="wrap" gap={12} w="100%">
                  {results.map(item => (
                    <ProductCard
                      key={item.id}
                      w={cardWidth}
                      imageUri={item.imageUrl}
                      title={item.title}
                      price={`${item.price.toFixed(2)} €`}
                      shopName={item.storeName || undefined}
                      onPress={() => navigation.navigate('ProductView', { productId: item.id })}
                    />
                  ))}
                </XStack>
              )}
            </YStack>
          </>
        )}

        <YStack h={32} />
      </ScrollView>
    </YStack>
  )
}
